// 予約機能で共通する利用予約に関する表示部分を定義
// 情報照会、変更内容確認で共有

import React from 'react'
import { Stack } from '@mui/material'
import { ElapsedMillisecond, formatYmdWeek, toApiHms } from '../../../../utils/dateUtil'
import { GItem, RowSpacerGItem } from '../grids'
import { LabelGItem, ValueGItem } from './reservationDetailParts'
import { GetUseReasonDto } from '../../../../dataAccess/webApi/dto/useReasonDto'
import { GetProjectDetailDto } from '../../../../dataAccess/webApi/dto/projectsDto'
import { ChildReservationInfoType, ParentInfoType, GoingTimeType, SelectAndOtherType } from '../../../../containers/common/reservation'
import ChildContent from "./childContent"
import CommonDetailContent from "./commonDetailContent"
import BasicDetailContent from "./basicDetailContent"
import { translate } from '../../../../i18n'
import { Flag, yesNo } from '../../../../containers/common/constant/classification'
import { getYesNo2Label } from '../../../../containers/common/codeMaster'

interface Reservation {
  facilityName: string
  
  useReasonCategory: string | null
  useReasonDetailCategory?: string | null
  useReasonDetailDescription?: string | null
  childInfoList: ChildReservationInfoType[]
  parentInfo: ParentInfoType
  // 事業マスタ
  projectMst: GetProjectDetailDto
  isUseReasonCategoryHidden: boolean
}

export interface ReservationConfirmation extends Reservation {
  lunchFlag: string
  snackFlag: string
  postponeCancelWaitFlag: string
  preferenceProvisionCertificateFlag?: string
  formUseReason?: SelectAndOtherType  
  citizenNote: string

  lunchAcceptFlag: string
  snackAcceptFlag: string
  postponeCancelWaitAcceptFlag: string
  immediatelyReservationFlag: string
}

export interface ReservationDelete extends Required<ReservationConfirmation> {
  goingTime: GoingTimeType
  usageDate: Date | ElapsedMillisecond
  useFromDatetime: Date | ElapsedMillisecond
  useToDatetime: Date | ElapsedMillisecond
  status: string
}

export interface ReservationReference extends ReservationDelete {
  reservationDatetime: Date
  reservationChangeDatetime?: Date
  isTodayConditionInputFacility?: string
  todayConditionInputFacilityName?: string | null
  todayConditionInputFacilityPublicFlag?: string
  todayConditionInputFacilityFields?: {
    bodyTemperature: string
    meal: string
    sleep: string
    defecation: string
  }
  todayConditionFacility?: {
    bodyTemperatureMeasurementTime1: string | null
    bodyTemperature1: number | null
    bodyTemperatureMeasurementTime2: string | null
    bodyTemperature2: number | null
    bodyTemperatureMeasurementTime3: string | null
    bodyTemperature3: number | null
    meal: string | null
    eatingMethod: string | null
    milkTime1: string | null
    milkAmount1: string | null
    milkTime2: string | null
    milkAmount2: string | null
    milkTime3: string | null
    milkAmount3: string | null
    mealTime1: string | null
    mealWhat1: string | null
    mealTime2: string | null
    mealWhat2: string | null
    sleep: string | null
    sleepFromTime1: string | null
    sleepToTime1: string | null
    sleepFromTime2: string | null
    sleepToTime2: string | null
    sleepFromTime3: string | null
    sleepToTime3: string | null
    defecation: string | null
    defecationHours1: string | null
    defecationCondition1: string | null
    defecationHours2: string | null
    defecationCondition2: string | null
    defecationHours3: string | null
    defecationCondition3: string | null
    todayConditionNote: string | null
    note: string | null
    status: string | null
    sendMailDatetime: string | null
    updateDatetime: string | null
  }
}

/** 入力時に必要なプロパティ */
interface Input {
  reservation: Reservation
  useReason: GetUseReasonDto
  displayType?: never
}
 
/** 入力内容確認時に必要なプロパティ */
interface Confirmation {
  reservation: ReservationConfirmation
  useReason: GetUseReasonDto
  displayType: 'confirmation'
}

/** 利用予約情報削除時の詳細照会に必要なプロパティ */
interface Delete {
  reservation: ReservationDelete
  useReason: GetUseReasonDto
  displayType: 'delete'
}

/** 詳細照会時に必要なプロパティ */
interface Reference {
  reservation: ReservationReference
  useReason: GetUseReasonDto
  displayType: 'reference'
}

export type ReservationDetailGItemsProps = Input | Confirmation | Delete | Reference;

/** 予約機能の利用予約情報コンテンツ部分 */
const ReservationDetailGItems = (props: ReservationDetailGItemsProps) => {
  const isShownReservationItem =
    props.displayType === 'confirmation' || props.displayType === 'delete' || props.displayType === 'reference'
  const isReference = props.displayType === 'reference'
  
  const childInfoList = props.reservation.childInfoList;

  let isTodayConditionPublic = false
  let isBodyTemperature: boolean = false
  let isMeal: boolean = false
  let isSleep: boolean = false
  let isDefecation: boolean = false
  let todayConditionFacilityUpdateDatetime = null

  if (props.displayType === 'reference') {
    isTodayConditionPublic =
      props.reservation.isTodayConditionInputFacility === yesNo.yes &&
      props.reservation.todayConditionInputFacilityPublicFlag === yesNo.yes &&
      props.reservation.todayConditionFacility?.status === '1'
    isBodyTemperature = props.reservation.todayConditionInputFacilityFields?.bodyTemperature === '1' || props.reservation.todayConditionInputFacilityFields?.bodyTemperature === '2'
    isMeal = props.reservation.todayConditionInputFacilityFields?.meal === '1' || props.reservation.todayConditionInputFacilityFields?.meal === '2'
    isSleep = props.reservation.todayConditionInputFacilityFields?.sleep === '1' || props.reservation.todayConditionInputFacilityFields?.sleep === '2'
    isDefecation = props.reservation.todayConditionInputFacilityFields?.defecation === '1' || props.reservation.todayConditionInputFacilityFields?.defecation === '2'
    todayConditionFacilityUpdateDatetime = props.reservation.todayConditionFacility?.updateDatetime ? new Date(props.reservation.todayConditionFacility.updateDatetime) : null
  }
  

  return (
    <>
      <BasicDetailContent {...props} />
      <RowSpacerGItem />

      {
        childInfoList.map((child, index) => (
          <React.Fragment key={index}>
            <ChildContent
              childInfo={child}
              projectMst={isShownReservationItem ? props.reservation.projectMst : undefined}
            />
            <RowSpacerGItem />
          </React.Fragment>
        ))
      }
      
      { isShownReservationItem && (
      <>
        <CommonDetailContent 
          lunchAcceptFlag={props.reservation.lunchAcceptFlag}
          lunchFlag={props.reservation.lunchFlag}
          snackAcceptFlag={props.reservation.snackAcceptFlag}
          snackFlag={props.reservation.snackFlag}
          immediatelyReservationFlag={props.reservation.immediatelyReservationFlag}
          postponeCancelWaitAcceptFlag={props.reservation.postponeCancelWaitAcceptFlag}
          postponeCancelWaitFlag={props.reservation.postponeCancelWaitFlag}
          projectMst={props.reservation.projectMst}
          preferenceProvisionCertificateFlag={props.reservation.preferenceProvisionCertificateFlag?? Flag.OFF}
          formUseReasonUsage={props.reservation.projectMst.useReasonInput}
          formUseReason={props.reservation.formUseReason?.stringify}
        />
        {/* 備考 */}
        <LabelGItem>{translate('system.reservationDetailGItems.label.note')}</LabelGItem>
        <ValueGItem>{props.reservation.citizenNote}</ValueGItem>        
      </>
      )}
      
      { isReference && (
        <>
          <RowSpacerGItem />
          <LabelGItem>{translate('system.reservationDetailGItems.label.reservationReceptionDatetime')}</LabelGItem>
          <ValueGItem>
            <Stack>
              <div>{formatYmdWeek(props.reservation.reservationDatetime)}</div>
              <div>{toApiHms(props.reservation.reservationDatetime)}</div>
            </Stack>
          </ValueGItem>
          { props.reservation.reservationChangeDatetime != null && (
            <>
              <LabelGItem>{translate('system.reservationDetailGItems.label.reservationChangeDatetime')}</LabelGItem>
              <ValueGItem>
                <Stack>
                  <div>{formatYmdWeek(props.reservation.reservationChangeDatetime)}</div>
                  <div>{toApiHms(props.reservation.reservationChangeDatetime)}</div>
                </Stack>
              </ValueGItem>
            </>
          )}
        </>
      )}

      {isReference && isTodayConditionPublic && (
        <>
          <RowSpacerGItem />
          <GItem xs={12}>{translate('system.reservationDetailGItems.label.todayConditionFacility.title', props.reservation.todayConditionInputFacilityName ? props.reservation.todayConditionInputFacilityName : '')}</GItem>
        </>
      )}

      {/* 検温時刻(1回目) */}
      {isReference && isTodayConditionPublic && isBodyTemperature && props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime1 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.bodyTemperatureMeasurementTime1')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime1}</ValueGItem>
        </>
      )}
      {/* 体温(1回目) */}
      {isReference && isTodayConditionPublic && isBodyTemperature && props.reservation.todayConditionFacility?.bodyTemperature1 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.bodyTemperature1')}</LabelGItem>
          <ValueGItem>{`${props.reservation.todayConditionFacility?.bodyTemperature1}℃`}</ValueGItem>
        </>
      )}
      {/* 検温時刻(2回目) */}
      {isReference && isTodayConditionPublic && isBodyTemperature && props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime2 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.bodyTemperatureMeasurementTime2')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime2}</ValueGItem>
        </>
      )}
      {/* 体温(2回目) */}
      {isReference && isTodayConditionPublic && isBodyTemperature && props.reservation.todayConditionFacility?.bodyTemperature2 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.bodyTemperature2')}</LabelGItem>
          <ValueGItem>{`${props.reservation.todayConditionFacility?.bodyTemperature2}℃`}</ValueGItem>
        </>
      )}
      {/* 検温時刻(3回目) */}
      {isReference && isTodayConditionPublic && isBodyTemperature && props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime3 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.bodyTemperatureMeasurementTime3')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime3}</ValueGItem>
        </>
      )}
      {/* 体温(3回目) */}
      {isReference && isTodayConditionPublic && isBodyTemperature && props.reservation.todayConditionFacility?.bodyTemperature3 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.bodyTemperature3')}</LabelGItem>
          <ValueGItem>{`${props.reservation.todayConditionFacility?.bodyTemperature3}℃`}</ValueGItem>
        </>
      )}

      {/* 食事セクションの改行 */}
      {isReference &&
        isTodayConditionPublic &&
        (isMeal && props.reservation.todayConditionFacility?.meal) &&
        (isBodyTemperature && (
          props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime1 ||
          props.reservation.todayConditionFacility?.bodyTemperature1 ||
          props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime2 ||
          props.reservation.todayConditionFacility?.bodyTemperature2 ||
          props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime3 ||
          props.reservation.todayConditionFacility?.bodyTemperature3
        )) && (
        <RowSpacerGItem />
      )}

      {/* 食事を取りましたか */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.meal && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.meal')}</LabelGItem>
          <ValueGItem>{getYesNo2Label(props.reservation.todayConditionFacility.meal)}</ValueGItem>
        </>
      )}
      {/* 栄養方法 */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.eatingMethod && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.eatingMethod')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.eatingMethod}</ValueGItem>
        </>
      )}
      {/* ミルク時刻(1回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.milkTime1 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.milkTime1')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.milkTime1}</ValueGItem>
        </>
      )}
      {/* ミルク量(1回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.milkAmount1 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.milkAmount1')}</LabelGItem>
          <ValueGItem>{`${props.reservation.todayConditionFacility?.milkAmount1}cc`}</ValueGItem>
        </>
      )}
      {/* ミルク時刻(2回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.milkTime2 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.milkTime2')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.milkTime2}</ValueGItem>
        </>
      )}
      {/* ミルク量(2回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.milkAmount2 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.milkAmount2')}</LabelGItem>
          <ValueGItem>{`${props.reservation.todayConditionFacility?.milkAmount2}cc`}</ValueGItem>
        </>
      )}
      {/* ミルク時刻(3回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.milkTime3 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.milkTime3')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.milkTime3}</ValueGItem>
        </>
      )}
      {/* ミルク量(3回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.milkAmount3 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.milkAmount3')}</LabelGItem>
          <ValueGItem>{`${props.reservation.todayConditionFacility?.milkAmount3}cc`}</ValueGItem>
        </>
      )}
      {/* 食事時刻(1回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.mealTime1 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.mealTime1')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.mealTime1}</ValueGItem>
        </>
      )}
      {/* 食事内容(1回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.mealWhat1 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.mealWhat1')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.mealWhat1}</ValueGItem>
        </>
      )}
      {/* 食事時刻(2回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.mealTime2 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.mealTime2')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.mealTime2}</ValueGItem>
        </>
      )}
      {/* 食事内容(2回目) */}
      {isReference && isTodayConditionPublic && isMeal && props.reservation.todayConditionFacility?.mealWhat2 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.mealWhat2')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.mealWhat2}</ValueGItem>
        </>
      )}

      {/* 睡眠セクションの改行 */}
      {isReference &&
        isTodayConditionPublic &&
        (isSleep && props.reservation.todayConditionFacility?.sleep) &&
        (
          (isBodyTemperature && (
            props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime1 ||
            props.reservation.todayConditionFacility?.bodyTemperature1 ||
            props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime2 ||
            props.reservation.todayConditionFacility?.bodyTemperature2 ||
            props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime3 ||
            props.reservation.todayConditionFacility?.bodyTemperature3
          )) ||
          (isMeal && props.reservation.todayConditionFacility?.meal)
        ) && (
        <RowSpacerGItem />
      )}

      {/* 睡眠をとりましたか */}
      {isReference && isTodayConditionPublic && isSleep && props.reservation.todayConditionFacility?.sleep && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.sleep')}</LabelGItem>
          <ValueGItem>{getYesNo2Label(props.reservation.todayConditionFacility.sleep)}</ValueGItem>
        </>
      )}
      {/* 睡眠時刻(1回目) */}
      {isReference && isTodayConditionPublic && isSleep && props.reservation.todayConditionFacility?.sleepFromTime1 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.sleepTime1')}</LabelGItem>
          <ValueGItem>{`${props.reservation.todayConditionFacility?.sleepFromTime1}～${props.reservation.todayConditionFacility?.sleepToTime1 ? props.reservation.todayConditionFacility.sleepToTime1 : ''}`}</ValueGItem>
        </>
      )}
      {/* 睡眠時刻(2回目) */}
      {isReference && isTodayConditionPublic && isSleep && props.reservation.todayConditionFacility?.sleepFromTime2 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.sleepTime2')}</LabelGItem>
          <ValueGItem>{`${props.reservation.todayConditionFacility?.sleepFromTime2}～${props.reservation.todayConditionFacility?.sleepToTime2 ? props.reservation.todayConditionFacility.sleepToTime2 : ''}`}</ValueGItem>
        </>
      )}
      {/* 睡眠時刻(3回目) */}
      {isReference && isTodayConditionPublic && isSleep && props.reservation.todayConditionFacility?.sleepFromTime3 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.sleepTime3')}</LabelGItem>
          <ValueGItem>{`${props.reservation.todayConditionFacility?.sleepFromTime3}～${props.reservation.todayConditionFacility?.sleepToTime3 ? props.reservation.todayConditionFacility.sleepToTime3 : ''}`}</ValueGItem>
        </>
      )}

      {/* 排便セクションの改行 */}
      {isReference &&
      isTodayConditionPublic &&
      (isDefecation && props.reservation.todayConditionFacility?.defecation) &&
      (
        (isBodyTemperature && (
          props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime1 ||
          props.reservation.todayConditionFacility?.bodyTemperature1 ||
          props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime2 ||
          props.reservation.todayConditionFacility?.bodyTemperature2 ||
          props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime3 ||
          props.reservation.todayConditionFacility?.bodyTemperature3
        )) ||
        (isMeal && props.reservation.todayConditionFacility?.meal) || 
        (isSleep && props.reservation.todayConditionFacility?.sleep)
      ) && (
        <RowSpacerGItem />
      )}

      {/* 排便しましたか */}
      {isReference && isTodayConditionPublic && isDefecation && props.reservation.todayConditionFacility?.defecation && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.defecation')}</LabelGItem>
          <ValueGItem>{getYesNo2Label(props.reservation.todayConditionFacility.defecation)}</ValueGItem>
        </>
      )}
      {/* 排便時刻(1回目) */}
      {isReference && isTodayConditionPublic && isDefecation && props.reservation.todayConditionFacility?.defecationHours1 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.defecationHours1')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.defecationHours1}</ValueGItem>
        </>
      )}
      {/* 排便の状態(1回目) */}
      {isReference && isTodayConditionPublic && isDefecation && props.reservation.todayConditionFacility?.defecationCondition1 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.defecationCondition1')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.defecationCondition1}</ValueGItem>
        </>
      )}
      {/* 排便時刻(2回目) */}
      {isReference && isTodayConditionPublic && isDefecation && props.reservation.todayConditionFacility?.defecationHours2 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.defecationHours2')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.defecationHours2}</ValueGItem>
        </>
      )}
      {/* 排便の状態(2回目) */}
      {isReference && isTodayConditionPublic && isDefecation && props.reservation.todayConditionFacility?.defecationCondition2 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.defecationCondition2')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.defecationCondition2}</ValueGItem>
        </>
      )}
      {/* 排便時刻(3回目) */}
      {isReference && isTodayConditionPublic && isDefecation && props.reservation.todayConditionFacility?.defecationHours3 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.defecationHours3')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.defecationHours3}</ValueGItem>
        </>
      )}
      {/* 排便の状態(3回目) */}
      {isReference && isTodayConditionPublic && isDefecation && props.reservation.todayConditionFacility?.defecationCondition3 && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.defecationCondition3')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.defecationCondition3}</ValueGItem>
        </>
      )}

      {/* 上記以外のセクションの改行 */}
      {isReference && isTodayConditionPublic &&
        (
          (isBodyTemperature && (
            props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime1 ||
            props.reservation.todayConditionFacility?.bodyTemperature1 ||
            props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime2 ||
            props.reservation.todayConditionFacility?.bodyTemperature2 ||
            props.reservation.todayConditionFacility?.bodyTemperatureMeasurementTime3 ||
            props.reservation.todayConditionFacility?.bodyTemperature3
          )) ||
          (isMeal && props.reservation.todayConditionFacility?.meal)||
          (isSleep && props.reservation.todayConditionFacility?.sleep) ||
          (isDefecation && props.reservation.todayConditionFacility?.defecation)
        ) && (
        <RowSpacerGItem />
      )}

      {/* 施設からの連絡 */}
      {isReference && isTodayConditionPublic && props.reservation.todayConditionFacility?.todayConditionNote && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.todayConditionNote')}</LabelGItem>
          <ValueGItem>{props.reservation.todayConditionFacility?.todayConditionNote}</ValueGItem>
        </>
      )}
      {/* 当日入力更新日時 */}
      {isReference && isTodayConditionPublic && todayConditionFacilityUpdateDatetime && (
        <>
          <LabelGItem>{translate('system.reservationDetailGItems.label.todayConditionFacility.updateDatetime', props.reservation.todayConditionInputFacilityName ? props.reservation.todayConditionInputFacilityName : '当日入力')}</LabelGItem>
          <ValueGItem>
            <Stack>
              <div>{formatYmdWeek(todayConditionFacilityUpdateDatetime)}</div>
              <div>{toApiHms(todayConditionFacilityUpdateDatetime)}</div>
            </Stack>
          </ValueGItem>
        </>
      )}

    </>
  )
}

export default ReservationDetailGItems
